import type { Vector3 } from 'three';

/**
 * The maths behind a bullet skipping off a surface.
 *
 * Everything here is pure and unit-tested: the angle a shot makes with a wall,
 * the chance it bounces at that angle, and the reflected direction. The weapon
 * owns the loop and the dice; this owns the geometry.
 */

/**
 * The angle between a shot's path and the surface plane, in radians.
 *
 * `0` is a graze — the path runs parallel to the wall — and `π/2` is a shot
 * driven straight into it, so the value grows as the hit gets more head-on.
 */
export function grazingAngle(direction: Vector3, normal: Vector3): number {
  const along = Math.min(1, Math.abs(direction.dot(normal)));
  return Math.max(0, Math.PI / 2 - Math.acos(along));
}

/**
 * The chance a shot at `grazing` bounces.
 *
 * A head-on hit (grazing at or past the critical angle) never bounces. Below
 * it the chance ramps up linearly to `maxChance` at a dead-parallel graze, so
 * the shallower the clip the likelier the skip.
 */
export function ricochetChance(grazing: number, criticalAngle: number, maxChance: number): number {
  if (criticalAngle <= 0 || grazing >= criticalAngle) return 0;
  return maxChance * (1 - grazing / criticalAngle);
}

/** Reflect a unit direction off a surface, returning the outgoing unit vector. */
export function reflect(direction: Vector3, normal: Vector3, out: Vector3): Vector3 {
  const along = direction.dot(normal);
  return out
    .copy(direction)
    .addScaledVector(normal, -2 * along)
    .normalize();
}

/**
 * Whether the hull gives way when a shot ends on it.
 *
 * A straight hit always breaches. A shot that only arrived by bouncing takes a
 * chance, so skipping a round off the hull is a risk rather than a loss.
 */
export function hullBreachesOnRicochet(ricocheted: boolean, roll: number, chance: number): boolean {
  return !ricocheted || roll < chance;
}
